import { Box, Space, SpaceVertical } from '@looker/components';
import { BIIcon, BIIconButton, globalTheme } from '@pulsepointinc/bi-components';
import { DsTypography } from '@pulsepointinc/design-system-react';
import { useEffect, useMemo, useRef } from 'react';
import { Markdown } from '../../../../components/Markdown';
import { useAIChatHistoryQuery } from '../../../../hooks/api';
import { useAIContext } from '../../AIProvider';
import { AIPrompt } from '../AIPrompt';
import { StyledTypingBubble, StyledTypingDot, StyledUserMessageBubble } from './styles';

interface AIChatProps {
  onClose: () => void;
}

export const AIChat: React.FC<AIChatProps> = ({ onClose }) => {
  const chatScrollContainer = useRef<HTMLDivElement | null>(null);
  const { activeChatId } = useAIContext();
  const { data } = useAIChatHistoryQuery({ workspaceId: 5196, chatId: activeChatId ?? 0 });

  const messages = useMemo(() => {
    return data?.messages ?? [];
  }, [data]);

  useEffect(() => {
    if (!chatScrollContainer.current || !messages.length) {
      return;
    }

    chatScrollContainer.current.scrollTop = chatScrollContainer.current.scrollHeight;
  }, [messages]);

  return (
    <SpaceVertical width="100%" height="100vh" gap="none">
      <Space between align="center" paddingY="u6" paddingX="u6" borderBottom={globalTheme.colors.outlineLight}>
        <DsTypography variant="h1">Studio AI</DsTypography>
        <BIIconButton shape="round" icon="Close" onClick={onClose} />
      </Space>

      <Box ref={chatScrollContainer} width="100%" height="100%" paddingX="u6" paddingY="u4" overflow="auto">
        {messages.length ? (
          <SpaceVertical minHeight="100%" justify="end">
            {messages.map((message) =>
              message.role === 'user' ? (
                <SpaceVertical key={message.message_id} gap="none" align="end" alignSelf="end" maxWidth={300}>
                  <StyledUserMessageBubble>
                    <Markdown>{message.content}</Markdown>
                  </StyledUserMessageBubble>
                </SpaceVertical>
              ) : message.message_id === -1 ? (
                <StyledTypingBubble key={message.message_id}>
                  <StyledTypingDot delay="0s" />
                  <StyledTypingDot delay="0.2s" />
                  <StyledTypingDot delay="0.4s" />
                </StyledTypingBubble>
              ) : (
                <Markdown key={message.message_id}>{message.content}</Markdown>
              ),
            )}
          </SpaceVertical>
        ) : (
          <SpaceVertical height="100%" align="center" justify="center">
            <Box padding="u4" backgroundColor={globalTheme.colors.backgroundLight} borderRadius="50%">
              <BIIcon icon="AiCow" size={64} />
            </Box>
            <DsTypography variant="h3" color="secondary" align="center">
              Where should we start?
            </DsTypography>
          </SpaceVertical>
        )}
      </Box>

      <Box width="100%" padding="u4" marginTop="auto">
        <AIPrompt />
      </Box>
    </SpaceVertical>
  );
};
